import React, {
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';
import { useAuth } from 'react-oidc-context';
import { SubscriptionAccessSnapshotDto, SubscriptionExpirationWarningDto } from './api';
import { subscribeToBirthRecordUpdated } from './features/birthRecordRefresh';
import useAstroApiClient from './hooks/useAstroApiClient';
import {
  createEmptyFeatureAccessMap,
  FeatureAccessMap,
  PlatformFeature,
} from './platform/features';

type SubscriptionStatus = 'active' | 'inactive' | 'unknown';

interface SubscriptionContextType {
  status: SubscriptionStatus;
  loading: boolean;
  features: FeatureAccessMap;
  expirationWarning: SubscriptionExpirationWarningDto | null;
  hasFeature: (feature: PlatformFeature) => boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  status: 'unknown',
  loading: true,
  features: createEmptyFeatureAccessMap(),
  expirationWarning: null,
  hasFeature: () => false,
  refresh: async () => undefined
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const auth = useAuth();
  const api = useAstroApiClient();
  const [status, setStatus] = useState<SubscriptionStatus>('unknown');
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<FeatureAccessMap>(createEmptyFeatureAccessMap);
  const [expirationWarning, setExpirationWarning] = useState<SubscriptionExpirationWarningDto | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const snapshot: SubscriptionAccessSnapshotDto = await api.apiSubscriptionAccess();
      const nextFeatures = createEmptyFeatureAccessMap();

      for (const featureAccess of snapshot.features ?? []) {
        if (featureAccess.feature) {
          nextFeatures[featureAccess.feature] = featureAccess.isEnabled === true;
        }
      }

      setFeatures(nextFeatures);
      setStatus(snapshot.hasActiveProSubscription === true ? 'active' : 'inactive');
      setExpirationWarning(snapshot.expirationWarning?.isEnabled === true ? snapshot.expirationWarning : null);
    } catch {
      setFeatures(createEmptyFeatureAccessMap());
      setStatus('inactive');
      setExpirationWarning(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (auth.isLoading) return;

    if (!auth.isAuthenticated) {
      setFeatures(createEmptyFeatureAccessMap());
      setStatus('inactive');
      setExpirationWarning(null);
      setLoading(false);
      return;
    }

    refresh();
  }, [auth.isLoading, auth.isAuthenticated, refresh]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return
    }

    return subscribeToBirthRecordUpdated(() => {
      void refresh()
    })
  }, [auth.isAuthenticated, refresh])

  return (
    <SubscriptionContext.Provider
      value={{
        status,
        loading,
        features,
        expirationWarning,
        hasFeature: (feature) => features[feature] === true,
        refresh,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

export default SubscriptionProvider;
